"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowLeft, Check, ImageIcon, Sparkles } from "lucide-react";
import { useTheme } from "next-themes";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { GoogleOAuthButton } from "@/components/auth/GoogleOAuthButton";
import { AuthDivider } from "@/components/auth/AuthDivider";
import { AccountConfirmationStep } from "@/components/auth/AccountConfirmationStep";
import { cn } from "@/lib/utils";
import {
  type AccountCredentialErrors,
  validateAccountCredentials,
} from "@/lib/auth/accountCredentialValidation";
import {
  clearCreditPurchaseAfterAuth,
  CREDIT_RESUME_ID_PARAM,
} from "@/utils/creditAuthIntentPersistence";
import { CREDIT_CHECKOUT_HANDOFF_ERROR } from "@/utils/creditCheckoutReturnUrl";
import SavePoint from "@/utils/savePoint";
import { trackEventWithJourney } from "@/utils/analytics";
import { useCreditCatalog } from "@/hooks/useCreditCatalog";
import { CreditPricingControls } from "@/components/credits/CreditPricingControls";
import { formatCreditPrice } from "@/constants/creditPacks";
import type { CreditModalContext } from "./creditModalContext";
import { CreateCreditPackOptions } from "./CreateCreditPackOptions";

const UNLOCK_EMAIL_ERROR_ID = "credit-unlock-email-error";
const UNLOCK_PASSWORD_HINT_ID = "credit-unlock-password-hint";
const UNLOCK_PASSWORD_ERROR_ID = "credit-unlock-password-error";

// Re-exported so existing importers keep working; the union itself now lives in
// its own module because it is referenced from the store, three hook prop
// types and this component, and eight hand-maintained copies had already
// drifted apart.
export type { CreditModalContext };

interface CreateCreditModalProps {
  open: boolean;
  context: CreditModalContext;
  creditPurchaseLoading: boolean;
  /** Preserve the selected pack when automatic checkout needs a manual retry. */
  checkoutRecoveryCredits?: number | null;
  onPurchase: (
    credits: number,
    price: number,
    stripePriceId: string
  ) => void | Promise<void>;
  /**
   * Footer button handler. When an image exists, this submits the front only;
   * otherwise it simply cancels. Not used for the X / Escape / overlay close.
   */
  onClose: () => void;
  /**
   * Handler for the X button, Escape key, and overlay click. Dismisses the
   * modal without submitting anything. Falls back to `onClose` when omitted.
   */
  onDismiss?: () => void;
  onFeedbackClick: () => void;
  onSocialShareClick: () => void;
  hasSubmittedFeedback: boolean | null;
  hasShared: boolean | null;
  hasImage: boolean;
  imageUrl?: string | null;
  hasShareablePastDesigns: boolean | null;
  canShowFeedbackReward: boolean;
  /** Free rewards require a permanent, email-confirmed account boundary. */
  canEarnRewards?: boolean;
  hideSocialReward?: boolean;
  /**
   * Has an ACCOUNT — false for an anonymous session, which is signed in but
   * has nothing to attach a purchase to.
   */
  isAuthenticated: boolean;
  /** Return customer-safe copy when navigation must remain on this dress. */
  onSignIn: (credits?: number, offerToken?: string) => string | void;
  /** Sign up for the bonus without selecting or purchasing a credit pack. */
  onSignUp?: () => string | void;
  /** Starts Google OAuth while preserving the selected pack and current dress. */
  onGoogleContinue?: (credits: number, offerToken?: string) => string | void;
  /**
   * Creates the account from the current anonymous session. Checkout starts
   * immediately only when confirmation is disabled; otherwise it resumes after
   * the email link is confirmed. Used by both guest credit gates.
   */
  onCreateAccountAndBuy?: (params: {
    email: string;
    password: string;
    credits: number;
    price: number;
    stripePriceId: string;
  }) => Promise<{
    ok: boolean;
    error?: string;
    /**
     * The address must be confirmed before checkout can begin.
     */
    requiresConfirmation?: boolean;
  }>;
  /** Reload-safe continuation for an inline account upgrade awaiting email. */
  pendingConfirmation?: {
    email: string;
    credits: number;
  } | null;
}

function getCreditModalMessage(context: CreditModalContext): string {
  switch (context) {
    case "anon_unlock":
      return "Create an account and add credits to generate a preview. Your dress and chat will stay with you.";
    case "generate_front":
    case "auto_check":
      return "Each generated image costs 1 credit.";
    case "generate_back":
      return "A back preview uses 1 credit. You can close this window to keep your current design.";
    case "modify":
      return "Each updated preview uses 1 credit. You can close this window to keep your current design.";
    case "chat_limit":
      // Shown to signed-in users only (the signed-out variant renders its own
      // "free messages" copy). This fires whenever the balance is zero,
      // which also covers a returning buyer who spent every credit — so keep
      // the wording about credits, not "free messages".
      return "Add credits to keep designing and chatting. Each new or updated preview uses 1 credit; chat uses no credits.";
    default:
      return "Each generated image costs 1 credit.";
  }
}

/**
 * Credit purchase modal using shadcn Dialog.
 * Uses semantic tokens (text-muted-foreground, border-border).
 */
export function CreateCreditModal({
  open,
  context,
  creditPurchaseLoading,
  checkoutRecoveryCredits = null,
  onPurchase,
  onClose,
  onDismiss,
  onFeedbackClick,
  onSocialShareClick,
  hasSubmittedFeedback,
  hasShared,
  hasImage,
  imageUrl = null,
  hasShareablePastDesigns,
  canShowFeedbackReward,
  canEarnRewards = true,
  hideSocialReward = false,
  isAuthenticated,
  onSignIn,
  onSignUp,
  onGoogleContinue,
  onCreateAccountAndBuy,
  pendingConfirmation = null,
}: CreateCreditModalProps) {
  const catalog = useCreditCatalog(open);
  const CREDIT_PACKS = catalog.packs;
  const CHEAPEST_CREDIT_PACK = CREDIT_PACKS[0];
  const pricingUnavailable = catalog.loading || Boolean(catalog.error);
  const isChatLimit = context === "chat_limit";
  // Guests can select a pack before authentication or take the separate
  // signup-bonus path without creating a checkout intent.
  const isAnonUnlock = context === "anon_unlock" || !isAuthenticated;
  const [recoveryDismissed, setRecoveryDismissed] = React.useState(false);
  const recoveryPack =
    isAuthenticated && !recoveryDismissed
      ? CREDIT_PACKS.find(
          pack => pack.credits === checkoutRecoveryCredits && pack.stripePriceId
        )
      : undefined;
  const suppressRewards = isChatLimit || isAnonUnlock || !canEarnRewards;
  const showFeedbackReward =
    !suppressRewards && canShowFeedbackReward && hasSubmittedFeedback === false;
  const canShareForReward = hasImage || hasShareablePastDesigns === true;
  const showSocialReward =
    !suppressRewards &&
    !hideSocialReward &&
    hasShared === false &&
    canShareForReward;
  const hasRewardActions = showSocialReward || showFeedbackReward;
  const rewardActionCount =
    Number(showSocialReward) + Number(showFeedbackReward);

  const [unlockEmail, setUnlockEmail] = React.useState("");
  const [unlockPassword, setUnlockPassword] = React.useState("");
  const [unlockFieldErrors, setUnlockFieldErrors] =
    React.useState<AccountCredentialErrors>({});
  const [unlockFormError, setUnlockFormError] = React.useState<string | null>(
    null
  );
  const [unlockSubmitting, setUnlockSubmitting] = React.useState(false);
  const [purchaseSubmitting, setPurchaseSubmitting] = React.useState(false);
  const [purchaseError, setPurchaseError] = React.useState<string | null>(null);
  const [unlockOAuthSubmitting, setUnlockOAuthSubmitting] =
    React.useState(false);
  const [ignorePendingConfirmation, setIgnorePendingConfirmation] =
    React.useState(false);
  const [unlockStep, setUnlockStep] = React.useState<
    "packs" | "account" | "confirm"
  >("packs");
  const [selectedUnlockPackCredits, setSelectedUnlockPackCredits] =
    React.useState(CHEAPEST_CREDIT_PACK.credits);
  const unlockEmailRef = React.useRef<HTMLInputElement>(null);
  const unlockPasswordRef = React.useRef<HTMLInputElement>(null);
  const unlockStepTitleRef = React.useRef<HTMLHeadingElement>(null);
  const selectedUnlockPackRef = React.useRef<HTMLButtonElement>(null);
  const previousUnlockStepRef = React.useRef<"packs" | "account" | "confirm">(
    "packs"
  );
  const unlockOAuthResetTimerRef = React.useRef<number | null>(null);
  const unlockOAuthSubmittingRef = React.useRef(false);
  const unlockSubmittingRef = React.useRef(false);
  const purchaseSubmittingRef = React.useRef(false);
  const { resolvedTheme } = useTheme();
  const pendingConfirmationPack =
    open && isAnonUnlock && pendingConfirmation && !ignorePendingConfirmation
      ? CREDIT_PACKS.find(pack => pack.credits === pendingConfirmation.credits)
      : undefined;
  const visibleUnlockStep = pendingConfirmationPack ? "confirm" : unlockStep;
  const visibleUnlockEmail = pendingConfirmationPack
    ? pendingConfirmation?.email || ""
    : unlockEmail;
  const selectedUnlockPack =
    pendingConfirmationPack ??
    CREDIT_PACKS.find(pack => pack.credits === selectedUnlockPackCredits) ??
    CHEAPEST_CREDIT_PACK;
  const purchaseTransitionBusy =
    unlockSubmitting ||
    unlockOAuthSubmitting ||
    purchaseSubmitting ||
    creditPurchaseLoading;

  React.useEffect(
    () => () => {
      if (unlockOAuthResetTimerRef.current !== null) {
        window.clearTimeout(unlockOAuthResetTimerRef.current);
      }
    },
    []
  );

  React.useEffect(() => {
    if (open) return;

    setUnlockEmail("");
    setUnlockPassword("");
    setUnlockFieldErrors({});
    setUnlockFormError(null);
    setUnlockSubmitting(false);
    unlockSubmittingRef.current = false;
    setPurchaseSubmitting(false);
    purchaseSubmittingRef.current = false;
    setPurchaseError(null);
    setRecoveryDismissed(false);
    setUnlockOAuthSubmitting(false);
    unlockOAuthSubmittingRef.current = false;
    setIgnorePendingConfirmation(false);
    if (unlockOAuthResetTimerRef.current !== null) {
      window.clearTimeout(unlockOAuthResetTimerRef.current);
      unlockOAuthResetTimerRef.current = null;
    }
    setUnlockStep("packs");
    setSelectedUnlockPackCredits(CHEAPEST_CREDIT_PACK.credits);
  }, [open, CHEAPEST_CREDIT_PACK.credits]);

  React.useEffect(() => {
    if (!open || !isAnonUnlock) {
      previousUnlockStepRef.current = visibleUnlockStep;
      return;
    }
    if (previousUnlockStepRef.current === visibleUnlockStep) return;
    previousUnlockStepRef.current = visibleUnlockStep;

    if (visibleUnlockStep === "packs") {
      selectedUnlockPackRef.current?.focus({ preventScroll: true });
      return;
    }
    // Focusing the changed heading both preserves keyboard position when the
    // prior control unmounts and announces the new step to screen readers.
    unlockStepTitleRef.current?.focus({ preventScroll: true });
  }, [isAnonUnlock, open, visibleUnlockStep]);

  const handleAuthNavigation = (navigate: () => string | void): boolean => {
    setUnlockFormError(null);
    try {
      const navigationError = navigate();
      if (navigationError) {
        setUnlockFormError(navigationError);
        return false;
      }
      return true;
    } catch {
      setUnlockFormError(
        "We couldn't open sign-in. Your dress is still here. Please try again."
      );
      return false;
    }
  };

  const signupAlternative =
    !isAuthenticated && onSignUp && visibleUnlockStep === "packs" ? (
      <Button
        type="button"
        variant="ghost"
        onClick={() => handleAuthNavigation(onSignUp)}
        className="min-h-11 h-auto min-w-0 whitespace-normal rounded-lg px-2 py-2 text-sm font-normal text-muted-foreground shadow-none"
      >
        <span>
          Or{" "}
          <span className="font-medium text-foreground underline underline-offset-4">
            sign up for 1 free credit
          </span>
        </span>
      </Button>
    ) : null;

  const handlePurchaseSelect = async (
    credits: number,
    price: number,
    stripePriceId: string
  ) => {
    if (purchaseSubmittingRef.current || creditPurchaseLoading) return;

    purchaseSubmittingRef.current = true;
    setPurchaseSubmitting(true);
    setPurchaseError(null);
    try {
      await onPurchase(credits, price, stripePriceId);
    } catch {
      // Storage and checkout setup errors can contain implementation details.
      // Keep the modal open and give the customer one safe recovery action.
      setPurchaseError(CREDIT_CHECKOUT_HANDOFF_ERROR);
    } finally {
      purchaseSubmittingRef.current = false;
      setPurchaseSubmitting(false);
    }
  };

  /**
   * Create the account and start checkout in one click.
   *
   * Order matters and is not interchangeable: the account must exist before
   * /api/credits/checkout, which is auth-required and reads the caller's user
   * id to attach the purchase. Because the upgrade happens IN PLACE, that id
   * is the same one that owns the dress on screen — which is what lets the
   * customer come back from Stripe to exactly this design.
   */
  const handleUnlockSelect = async (
    credits: number,
    price: number,
    stripePriceId: string
  ) => {
    // React state does not update synchronously, so it cannot prevent two
    // activations in the same event turn from creating/upgrading the account
    // and opening checkout twice. Keep the in-flight claim in a ref as well.
    if (unlockSubmittingRef.current) return;
    const validation = validateAccountCredentials(unlockEmail, unlockPassword);
    setUnlockFieldErrors(validation.errors);
    setUnlockFormError(null);
    if (!validation.isValid) {
      const firstInvalidField = validation.errors.email
        ? unlockEmailRef.current
        : unlockPasswordRef.current;
      firstInvalidField?.focus({ preventScroll: true });
      firstInvalidField?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (!onCreateAccountAndBuy) {
      setUnlockFormError("Something went wrong. Please try again.");
      return;
    }

    unlockSubmittingRef.current = true;
    setUnlockSubmitting(true);
    let checkoutStarted = false;
    try {
      const result = await onCreateAccountAndBuy({
        email: validation.normalizedEmail,
        password: unlockPassword,
        credits,
        price,
        stripePriceId,
      });
      if (!result.ok) {
        setUnlockFormError(
          result.error ?? "Something went wrong. Please try again."
        );
      } else if (result.requiresConfirmation) {
        setUnlockPassword("");
        setUnlockStep("confirm");
      } else {
        checkoutStarted = true;
      }
      // On success the caller replaces this gate with checkout; leave the
      // spinner up rather than flashing an idle form during that transition.
    } catch {
      setUnlockFormError("Something went wrong. Please try again.");
    } finally {
      // A successful no-confirmation result has already replaced this modal
      // with checkout. Keep the one-shot claim until `open` becomes false;
      // releasing it here creates a duplicate-submit window during that render.
      if (!checkoutStarted) {
        unlockSubmittingRef.current = false;
        setUnlockSubmitting(false);
      }
    }
  };

  const clearPendingConfirmationIntent = () => {
    const url = new URL(window.location.href);
    const intentId = url.searchParams.get(CREDIT_RESUME_ID_PARAM);
    clearCreditPurchaseAfterAuth(intentId);
    if (intentId) SavePoint.clear(intentId);
    url.searchParams.delete("credit_intent");
    url.searchParams.delete(CREDIT_RESUME_ID_PARAM);
    url.searchParams.delete("credit_pack");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  };

  const handleConfirmationRecovery = () => {
    clearPendingConfirmationIntent();
    setIgnorePendingConfirmation(true);
    setUnlockEmail("");
    setUnlockPassword("");
    setUnlockFieldErrors({});
    setUnlockFormError(null);
    setUnlockStep("account");
  };

  const handleOpenChange = (o: boolean) => {
    if (o || purchaseTransitionBusy) return;
    if (visibleUnlockStep === "confirm") {
      clearPendingConfirmationIntent();
    }
    (onDismiss ?? onClose)();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        title={null}
        showCloseButton={!purchaseTransitionBusy}
        onEscapeKeyDown={event => {
          if (purchaseTransitionBusy) event.preventDefault();
        }}
        onPointerDownOutside={event => {
          if (purchaseTransitionBusy) event.preventDefault();
        }}
        style={{
          maxHeight:
            "calc(100dvh - env(safe-area-inset-top) - max(env(safe-area-inset-bottom), var(--keyboard-inset, 0px)) - 1rem)",
          top: "calc(50% + (env(safe-area-inset-top) - max(env(safe-area-inset-bottom), var(--keyboard-inset, 0px))) / 2)",
        }}
        className={cn(
          "grid max-h-[calc(100svh-1rem)] w-[calc(100vw-2rem)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden !gap-0 !rounded-2xl !p-0",
          isAnonUnlock ? "sm:max-w-md" : "sm:max-w-xl"
        )}
      >
        <DialogHeader
          className={cn(
            "relative shrink-0 border-b border-border px-4 pb-3 pt-4 text-left",
            isAnonUnlock &&
              "[@media(max-height:620px)]:pb-2 [@media(max-height:620px)]:pt-3"
          )}
        >
          {isAnonUnlock && visibleUnlockStep === "account" ? (
            <button
              type="button"
              aria-label="Back to credit packs"
              onClick={() => {
                setUnlockFormError(null);
                setUnlockStep("packs");
              }}
              disabled={
                unlockSubmitting ||
                unlockOAuthSubmitting ||
                creditPurchaseLoading
              }
              className="absolute left-2 top-2 inline-flex size-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:left-3 sm:top-3"
            >
              <ArrowLeft className="size-4" aria-hidden />
            </button>
          ) : null}
          <DialogTitle
            ref={unlockStepTitleRef}
            tabIndex={isAnonUnlock ? -1 : undefined}
            aria-live={isAnonUnlock ? "polite" : undefined}
            aria-label={
              isAnonUnlock
                ? visibleUnlockStep === "packs"
                  ? "Add credits"
                  : visibleUnlockStep === "account"
                    ? "Create your account"
                    : "Check your email"
                : undefined
            }
            className={cn(
              "pr-10 text-xl font-semibold tracking-normal text-foreground",
              isAnonUnlock && visibleUnlockStep === "account" && "pl-9"
            )}
          >
            {isAnonUnlock
              ? visibleUnlockStep === "packs"
                ? "Add credits"
                : visibleUnlockStep === "account"
                  ? "Create your account"
                  : "Check your email"
              : recoveryPack
                ? "Continue to checkout"
                : isChatLimit
                  ? "Buy credits to continue"
                  : "You’re out of credits"}
          </DialogTitle>
          <DialogDescription
            className={cn(
              "max-w-lg text-sm leading-5 text-muted-foreground",
              isAnonUnlock && visibleUnlockStep === "account" && "pl-9",
              isAnonUnlock && visibleUnlockStep === "confirm" && "sr-only",
              isAnonUnlock && "[@media(max-height:620px)]:hidden",
              !isAnonUnlock && "[@media(max-height:620px)]:hidden"
            )}
          >
            {isAnonUnlock
              ? visibleUnlockStep === "packs"
                ? hasImage
                  ? "Each back preview or updated image uses 1 credit."
                  : "Each preview uses 1 credit. Choose a pack to keep designing."
                : visibleUnlockStep === "account"
                  ? "Next, you’ll continue to secure checkout."
                  : "Confirm your email to continue to checkout."
              : recoveryPack
                ? "Your account is ready. Continue with the pack you selected."
                : getCreditModalMessage(context)}
          </DialogDescription>
        </DialogHeader>
        {pricingUnavailable ? (
          <div className="px-4 py-5">
            <CreditPricingControls catalog={catalog} />
            {signupAlternative ? (
              <div className="mt-3 text-center">{signupAlternative}</div>
            ) : null}
            {unlockFormError ? (
              <p
                role="alert"
                className="mt-2 text-center text-xs text-destructive"
              >
                {unlockFormError}
              </p>
            ) : null}
          </div>
        ) : isAnonUnlock ? (
          visibleUnlockStep === "packs" ? (
            <div className="min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
              <div className="mx-auto grid max-w-sm gap-2.5">
                <div className="flex items-center gap-3 rounded-xl bg-muted/40 p-2">
                  <div
                    data-testid="credit-design-preview"
                    className="relative aspect-[4/5] w-11 shrink-0 overflow-hidden rounded-lg border border-border bg-muted shadow-sm [@media(max-height:620px)]:w-9"
                  >
                    {hasImage && imageUrl ? (
                      <Image
                        src={imageUrl}
                        alt="The dress you just designed"
                        fill
                        sizes="44px"
                        className="object-cover object-top"
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center bg-gradient-to-br from-brand-logo/15 to-primary-muted">
                        <span
                          data-testid="credit-design-placeholder-icon"
                          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-background/80 text-brand-logo shadow-sm"
                        >
                          <Sparkles className="size-4 shrink-0" aria-hidden />
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
                      <Check
                        className="size-4 shrink-0 text-success"
                        aria-hidden
                      />
                      Dress saved
                    </p>
                    <p className="text-xs leading-4 text-muted-foreground">
                      Your design and chat stay with you.
                    </p>
                  </div>
                </div>

                <CreditPricingControls catalog={catalog} />
                <div
                  role="group"
                  aria-label="Choose a credit pack"
                  className="grid gap-2"
                >
                  {CREDIT_PACKS.map(pack => {
                    const isSelected =
                      pack.credits === selectedUnlockPack.credits;
                    const isUnavailable = !pack.stripePriceId;
                    const badge =
                      pack.badge === "most-popular"
                        ? "Recommended"
                        : pack.badge === "best-value"
                          ? "Best value"
                          : null;

                    return (
                      <button
                        ref={isSelected ? selectedUnlockPackRef : undefined}
                        key={pack.credits}
                        type="button"
                        aria-pressed={isSelected}
                        aria-label={`Select ${pack.credits} credits for ${formatCreditPrice(pack.price, pack.currency)}`}
                        disabled={isUnavailable}
                        onClick={() => {
                          setSelectedUnlockPackCredits(pack.credits);
                          setUnlockFormError(null);
                        }}
                        className={cn(
                          "grid min-h-11 min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl border px-3 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
                          isSelected
                            ? "border-primary bg-primary-muted/60 ring-1 ring-primary/20"
                            : "border-border bg-background hover:border-primary/40"
                        )}
                      >
                        <span className="min-w-0">
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">
                              {pack.credits} credits
                            </span>
                            {badge ? (
                              <span className="rounded-full bg-warning-muted px-1.5 py-0.5 text-[9px] font-semibold text-warning">
                                {badge}
                              </span>
                            ) : null}
                          </span>
                          <span className="block text-[11px] text-muted-foreground sm:text-xs">
                            {formatCreditPrice(
                              pack.price / pack.credits,
                              pack.currency
                            )}{" "}
                            per image
                          </span>
                        </span>
                        <span className="text-right">
                          <span className="block text-sm font-semibold tabular-nums text-foreground">
                            {formatCreditPrice(pack.price, pack.currency)}
                          </span>
                          <span
                            className={cn(
                              "text-[11px] font-medium",
                              isSelected
                                ? "text-primary"
                                : "text-muted-foreground"
                            )}
                          >
                            {isSelected ? "Selected" : "Choose"}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="grid gap-1.5 border-t border-border pt-3 [@media(max-height:620px)]:pt-2">
                  <Button
                    type="button"
                    variant="gradient"
                    onClick={() => {
                      setUnlockFormError(null);
                      trackEventWithJourney("credit_pack_selected", {
                        credits: selectedUnlockPack.credits,
                        price: selectedUnlockPack.price,
                        credit_modal_context: context,
                        authentication_required: true,
                      });
                      setUnlockStep("account");
                    }}
                    disabled={!selectedUnlockPack.stripePriceId}
                    className="h-11 w-full rounded-xl px-3 text-sm font-semibold sm:text-base"
                    aria-label={`Continue with ${selectedUnlockPack.credits} credits for ${formatCreditPrice(selectedUnlockPack.price, selectedUnlockPack.currency)}`}
                  >
                    Continue with {selectedUnlockPack.credits} credits ·{" "}
                    {formatCreditPrice(
                      selectedUnlockPack.price,
                      selectedUnlockPack.currency
                    )}
                  </Button>
                  <p className="text-center text-[11px] leading-4 text-muted-foreground [@media(max-height:620px)]:hidden">
                    Create your account next. No charge yet.
                  </p>
                  <div className="grid grid-cols-1 gap-1">
                    {signupAlternative}
                    {hasImage ? (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={onClose}
                        aria-label="Free quote · 0 credits"
                        className="min-h-11 min-w-0 px-2 text-xs font-semibold text-primary shadow-none"
                      >
                        Free quote · 0 credits
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() =>
                        handleAuthNavigation(() =>
                          onSignIn(
                            selectedUnlockPack.credits,
                            selectedUnlockPack.stripePriceId
                          )
                        )
                      }
                      aria-label="Already have an account? Sign in"
                      className="min-h-11 min-w-0 px-2 text-xs font-semibold text-muted-foreground shadow-none"
                    >
                      Already have an account? Sign in
                    </Button>
                  </div>
                  {unlockFormError ? (
                    <p
                      role="alert"
                      className="text-center text-xs leading-4 text-destructive"
                    >
                      {unlockFormError}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : visibleUnlockStep === "account" ? (
            <div
              data-testid="credit-account-scroll-region"
              className="min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 [@media(max-height:620px)]:pt-2.5"
            >
              <div className="mx-auto max-w-sm space-y-2.5">
                <div className="flex items-center justify-between gap-3 rounded-xl bg-primary-muted/45 px-3 py-2 [@media(max-height:620px)]:py-1.5">
                  <p className="text-sm font-semibold text-foreground">
                    {selectedUnlockPack.credits} credits selected
                  </p>
                  <p className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                    {formatCreditPrice(
                      selectedUnlockPack.price,
                      selectedUnlockPack.currency
                    )}
                  </p>
                </div>

                <div className="[&>button]:h-11 [@media(max-height:620px)]:[&>button]:mb-2 [@media(max-height:620px)]:[&>div]:mb-2">
                  <GoogleOAuthButton
                    onClick={() => {
                      if (unlockOAuthSubmittingRef.current) return;
                      unlockOAuthSubmittingRef.current = true;
                      setUnlockOAuthSubmitting(true);
                      const navigationStarted = handleAuthNavigation(() =>
                        onGoogleContinue?.(
                          selectedUnlockPack.credits,
                          selectedUnlockPack.stripePriceId
                        )
                      );
                      if (!navigationStarted) {
                        unlockOAuthSubmittingRef.current = false;
                        setUnlockOAuthSubmitting(false);
                        return;
                      }
                      // Navigation normally replaces this page immediately.
                      // If the browser blocks it, return the modal to an
                      // actionable state instead of trapping every control.
                      unlockOAuthResetTimerRef.current = window.setTimeout(
                        () => {
                          unlockOAuthSubmittingRef.current = false;
                          setUnlockOAuthSubmitting(false);
                        },
                        10_000
                      );
                    }}
                    disabled={
                      !onGoogleContinue ||
                      unlockSubmitting ||
                      creditPurchaseLoading
                    }
                    loading={unlockOAuthSubmitting}
                    isDarkMode={resolvedTheme === "dark"}
                  />
                  <AuthDivider />
                </div>

                <form
                  className="space-y-3 [@media(max-height:620px)]:space-y-2"
                  onSubmit={event => {
                    event.preventDefault();
                    if (!selectedUnlockPack.stripePriceId) return;
                    void handleUnlockSelect(
                      selectedUnlockPack.credits,
                      selectedUnlockPack.price,
                      selectedUnlockPack.stripePriceId
                    );
                  }}
                >
                  <div className="space-y-1">
                    <label
                      htmlFor="credit-unlock-email"
                      className="block text-xs font-medium text-foreground sm:text-sm"
                    >
                      Email
                    </label>
                    <input
                      ref={unlockEmailRef}
                      id="credit-unlock-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={unlockEmail}
                      onChange={event => {
                        setUnlockEmail(event.target.value);
                        setUnlockFieldErrors(current => ({
                          ...current,
                          email: undefined,
                        }));
                        setUnlockFormError(null);
                      }}
                      disabled={
                        unlockSubmitting ||
                        unlockOAuthSubmitting ||
                        creditPurchaseLoading
                      }
                      aria-invalid={Boolean(unlockFieldErrors.email)}
                      aria-describedby={
                        unlockFieldErrors.email
                          ? UNLOCK_EMAIL_ERROR_ID
                          : undefined
                      }
                      placeholder="you@example.com"
                      className="h-11 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    {unlockFieldErrors.email ? (
                      <span
                        id={UNLOCK_EMAIL_ERROR_ID}
                        role="alert"
                        className="block text-sm text-destructive"
                      >
                        {unlockFieldErrors.email}
                      </span>
                    ) : null}
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="credit-unlock-password"
                      className="block text-xs font-medium text-foreground sm:text-sm"
                    >
                      Password
                    </label>
                    <input
                      ref={unlockPasswordRef}
                      id="credit-unlock-password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      value={unlockPassword}
                      onChange={event => {
                        setUnlockPassword(event.target.value);
                        setUnlockFieldErrors(current => ({
                          ...current,
                          password: undefined,
                        }));
                        setUnlockFormError(null);
                      }}
                      disabled={
                        unlockSubmitting ||
                        unlockOAuthSubmitting ||
                        creditPurchaseLoading
                      }
                      aria-invalid={Boolean(unlockFieldErrors.password)}
                      aria-describedby={
                        unlockFieldErrors.password
                          ? `${UNLOCK_PASSWORD_HINT_ID} ${UNLOCK_PASSWORD_ERROR_ID}`
                          : UNLOCK_PASSWORD_HINT_ID
                      }
                      minLength={8}
                      placeholder="Create a password"
                      className="h-11 w-full rounded-lg border border-border bg-background px-3 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                    <span
                      id={UNLOCK_PASSWORD_HINT_ID}
                      className="block text-[11px] leading-4 text-muted-foreground sm:text-xs"
                    >
                      <span className="[@media(max-height:620px)]:hidden">
                        At least 8 characters, with uppercase and lowercase
                        letters and a number
                      </span>
                      <span className="hidden whitespace-nowrap text-[10px] [@media(max-height:620px)]:inline">
                        8+ characters · A–Z, a–z, 0–9
                      </span>
                    </span>
                    {unlockFieldErrors.password ? (
                      <span
                        id={UNLOCK_PASSWORD_ERROR_ID}
                        role="alert"
                        className="block text-sm text-destructive"
                      >
                        {unlockFieldErrors.password}
                      </span>
                    ) : null}
                  </div>

                  {unlockFormError ? (
                    <p role="alert" className="text-sm text-destructive">
                      {unlockFormError}
                    </p>
                  ) : null}

                  <Button
                    type="submit"
                    variant="gradient"
                    disabled={
                      unlockSubmitting ||
                      unlockOAuthSubmitting ||
                      creditPurchaseLoading ||
                      !selectedUnlockPack.stripePriceId
                    }
                    className="h-11 w-full rounded-xl px-3 text-sm font-semibold whitespace-nowrap sm:h-12 sm:text-base"
                    aria-label={`Continue with ${selectedUnlockPack.credits} credits · ${formatCreditPrice(selectedUnlockPack.price, selectedUnlockPack.currency)}`}
                  >
                    {unlockSubmitting ? (
                      <>
                        <LoadingSpinner size="sm" /> Opening checkout…
                      </>
                    ) : (
                      <>
                        Continue with {selectedUnlockPack.credits} credits ·{" "}
                        {formatCreditPrice(
                          selectedUnlockPack.price,
                          selectedUnlockPack.currency
                        )}
                      </>
                    )}
                  </Button>
                </form>

                <div className="border-t border-border pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      handleAuthNavigation(() =>
                        onSignIn(
                          selectedUnlockPack.credits,
                          selectedUnlockPack.stripePriceId
                        )
                      )
                    }
                    disabled={
                      unlockSubmitting ||
                      unlockOAuthSubmitting ||
                      creditPurchaseLoading
                    }
                    className="min-h-11 w-full px-2 text-xs font-semibold text-primary shadow-none"
                  >
                    Already have an account? Sign in
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="min-h-0 overflow-y-auto">
              <AccountConfirmationStep
                email={visibleUnlockEmail}
                description="Open the confirmation link sent to"
                detail={
                  <div className="rounded-xl border border-primary/20 bg-primary-muted/45 px-3 py-2 text-left">
                    <p className="text-xs font-medium text-muted-foreground">
                      Selected pack
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      {selectedUnlockPack.credits} credits ·{" "}
                      {formatCreditPrice(
                        selectedUnlockPack.price,
                        selectedUnlockPack.currency
                      )}
                    </p>
                  </div>
                }
                footnote={
                  <>
                    You won’t be charged until you complete checkout with
                    Stripe.
                  </>
                }
                recoveryLabel="Use a different email"
                onRecovery={handleConfirmationRecovery}
              />
            </div>
          )
        ) : creditPurchaseLoading ? (
          <div className="flex flex-col items-center justify-center space-y-4 px-5 py-12 sm:px-6">
            <LoadingSpinner size="md" />
            <p className="text-center text-sm text-muted-foreground">
              Opening secure checkout…
            </p>
          </div>
        ) : recoveryPack ? (
          <div className="min-h-0 space-y-3 overflow-y-auto px-4 py-5 sm:px-6">
            <p className="font-semibold">
              {recoveryPack.credits} credits ·{" "}
              {formatCreditPrice(recoveryPack.price, recoveryPack.currency)}
            </p>
            <Button
              className="min-h-11 w-full"
              disabled={purchaseSubmitting}
              onClick={() =>
                void handlePurchaseSelect(
                  recoveryPack.credits,
                  recoveryPack.price,
                  recoveryPack.stripePriceId!
                )
              }
            >
              {purchaseSubmitting
                ? "Opening checkout…"
                : `Continue to checkout · ${formatCreditPrice(recoveryPack.price, recoveryPack.currency)}`}
            </Button>
            {purchaseError ? (
              <p role="alert" className="text-sm text-destructive">
                {purchaseError}
              </p>
            ) : null}
            <Button
              variant="ghost"
              className="min-h-11 w-full"
              disabled={purchaseSubmitting}
              onClick={() => setRecoveryDismissed(true)}
            >
              Choose a different pack
            </Button>
          </div>
        ) : (
          <div
            data-testid="credit-pack-scroll-region"
            className="min-h-0 space-y-4 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 sm:space-y-5 sm:px-6 sm:pb-6 sm:pt-4"
          >
            <section className="space-y-2 sm:space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold text-foreground">
                  Choose a pack
                </h3>
                <span className="rounded-full border border-border px-3 py-1 text-xs font-medium text-foreground">
                  Secure checkout
                </span>
              </div>
              <CreditPricingControls catalog={catalog} />
              <CreateCreditPackOptions
                packs={CREDIT_PACKS}
                onSelect={handlePurchaseSelect}
                onSelectError={() =>
                  setPurchaseError(CREDIT_CHECKOUT_HANDOFF_ERROR)
                }
                disabled={creditPurchaseLoading || purchaseSubmitting}
                compact
              />
              {purchaseError ? (
                <p role="alert" className="text-sm text-destructive">
                  {purchaseError}
                </p>
              ) : null}
            </section>

            {hasRewardActions && (
              <section
                data-testid="credit-reward-alternatives"
                className="hidden space-y-3 border-t border-border pt-4 sm:block"
              >
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Earn free credits
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {rewardActionCount > 1
                      ? "Choose an option to earn credits and keep designing."
                      : "Earn credits to keep designing."}
                  </p>
                </div>
                <div className="grid gap-2">
                  {showSocialReward && (
                    <Button
                      variant="gradient"
                      onClick={onSocialShareClick}
                      disabled={creditPurchaseLoading}
                      className="h-12 w-full rounded-lg text-base font-semibold"
                    >
                      {hasImage
                        ? "Share your design (+2 credits)"
                        : "Share a saved design (+2 credits)"}
                    </Button>
                  )}
                  {showFeedbackReward && (
                    <Button
                      variant="outline"
                      onClick={onFeedbackClick}
                      disabled={creditPurchaseLoading}
                      className="h-12 w-full rounded-lg bg-background text-base font-semibold shadow-none hover:bg-muted/60"
                    >
                      Share feedback (+1 credit)
                    </Button>
                  )}
                </div>
              </section>
            )}

            <div
              data-testid="credit-nonpurchase-alternatives"
              className={cn(
                "space-y-2 border-t border-border pt-2 sm:space-y-3 sm:pt-4",
                !hasImage && "hidden sm:block"
              )}
            >
              {hasImage ? (
                <>
                  <p className="hidden text-sm leading-5 text-muted-foreground sm:block">
                    Get a free quote for your current design. No credits or
                    commitment needed.
                  </p>
                  <Button
                    onClick={onClose}
                    disabled={creditPurchaseLoading}
                    variant="outline"
                    className="h-10 w-full rounded-lg text-sm font-semibold shadow-none sm:h-12 sm:text-base"
                  >
                    Use this design for a free quote
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground [@media(max-height:620px)]:hidden">
                    Browse finished designs in the gallery for more ideas.
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="h-12 w-full rounded-xl border-primary/35 bg-primary/10 text-base font-semibold text-primary shadow-sm shadow-primary/10 hover:border-primary/60 hover:bg-primary/15 hover:text-primary [@media(max-height:620px)]:h-10"
                  >
                    <Link href="/gallery">
                      <ImageIcon className="size-4" aria-hidden />
                      View existing designs
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={creditPurchaseLoading}
                    className="h-12 w-full rounded-lg text-base font-semibold shadow-none [@media(max-height:620px)]:h-10"
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
